import type { DecoratedEvent } from '@/features/events/types';

export type EventExportRow = DecoratedEvent['exports'][number];

/**
 * Pure lookups over the EventExport rows the events queries already carry
 * (selected via EventExportFragment). Rows are viewer-scoped by the server,
 * an anonymous request gets none, so their presence already encodes
 * authorization and nothing here needs an auth gate.
 */
export const collectEventExportRows = (
  events: DecoratedEvent[]
): EventExportRow[] => events.flatMap((event) => event.exports);

export const getExportsForCalendar = (
  rows: EventExportRow[],
  calendarId: string
): EventExportRow[] => rows.filter((row) => row.calendarId === calendarId);

/**
 * Whether the event's rows lag its current version, optionally scoped to one
 * calendar. A row lags when its sequence trails the event's version, and
 * auto-updating rows never lag (the server pushes changes to them). A null
 * sequence is deliberately NOT stale here — the asymmetric twin of
 * getIntegrationExportStatus, which treats null as stale.
 */
export const hasStaleExport = (
  rows: EventExportRow[],
  eventId: string,
  eventVersion: number | null,
  calendarId?: string
): boolean => {
  if (eventVersion == null) return false;
  let eventExports = rows.filter((row) => row.eventId === eventId);
  if (calendarId) {
    eventExports = eventExports.filter((row) => row.calendarId === calendarId);
  }
  return eventExports.some(
    (row) =>
      !row.autoUpdate && row.sequence != null && row.sequence < eventVersion
  );
};
